import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_API_URL;

interface PlayerFinancesProps {
  leagueId: number;
  onBack: () => void;
}

interface PlayerFinance {
  player_id: number;
  player_name: string;
  registration_status: string;
  total_paid: number;
  total_earned: number;
  balance: number;
}

interface LeaguePotBalance {
  league_pot: number;
  payouts_made: number;
  balance: number;
}

interface PaymentHistory {
  id: number;
  payment_type: string;
  amount: number;
  refunded: boolean;
  payment_method: string;
  paid_at: string;
  item_name: string;
}

interface PayoutHistory {
  id: number;
  amount: number;
  source: string;
  overturned: boolean;
  payout_date: string;
  notes: string;
}

interface PlayerFinancialDetails {
  playerInfo: {
    player_id: number;
    player_name: string;
    email: string;
    bowler_id: number;
    bowler_name: string;
    registration_status: string;
    registration_date: string;
    payment_complete: boolean;
    team_id: number;
    team_name: string;
  };
  paymentHistory: PaymentHistory[];
  payoutHistory: PayoutHistory[];
  financialSummary: {
    totalPaid: string;
    totalEarned: string;
    balance: string;
    isEarning: boolean;
  };
}

const PlayerFinances: React.FC<PlayerFinancesProps> = ({ leagueId, onBack }) => {
  const { t } = useTranslation();
  const [playersFinancial, setPlayersFinancial] = useState<PlayerFinance[]>([]);
  const [leaguePotBalance, setLeaguePotBalance] = useState<LeaguePotBalance | null>(null);
  const [selectedPlayer, setSelectedPlayer] = useState<PlayerFinancialDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showReverseModal, setShowReverseModal] = useState(false);
  const [reversePayoutId, setReversePayoutId] = useState<number | null>(null);
  const [reverseNotes, setReverseNotes] = useState('');
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundPaymentId, setRefundPaymentId] = useState<any>(null);
  const [refundPaymentType, setRefundPaymentType] = useState<any>(null);


  const openReverseModal = (payoutId: number) => {
    setReversePayoutId(payoutId);
    setReverseNotes('');
    setShowReverseModal(true);
  };

  const closeReverseModal = () => {
    setShowReverseModal(false);
    setReversePayoutId(null);
    setReverseNotes('');
  };

  const confirmReversePayout = () => {
    if (reversePayoutId !== null) {
      handleReversePayout(reversePayoutId, reverseNotes);
      closeReverseModal();
    }
  };

  const openRefundModal = (paymentId: number, paymentType: string) => {
    setRefundPaymentId(paymentId);
    setRefundPaymentType(paymentType);
    setShowRefundModal(true);
  };

  const closeRefundModal = () => {
    setShowRefundModal(false);
    setRefundPaymentId(null);
    setRefundPaymentType(null);
  };

  const confirmRefundPayment = () => {
    if (refundPaymentId !== null && refundPaymentType !== null) {
      handleRefundPayment(refundPaymentId, refundPaymentType);
      closeRefundModal();
    }
  };

  const fetchPlayersFinancialSummary = useCallback(async () => {
    try {
      setLoading(true);
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

      const response = await fetch(`${apiUrl}/admin/bowler-finances/leagues/${leagueId}/players/financial-summary`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch players financial summary');
      }

      const data = await response.json();
      setPlayersFinancial(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  const fetchLeaguePotBalance = useCallback(async () => {
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

      const response = await fetch(`${apiUrl}/admin/bowler-finances/leagues/${leagueId}/pot-balance`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch league pot balance');
      }

      const data = await response.json();
      setLeaguePotBalance(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    }
  }, [leagueId]);

  useEffect(() => {
    fetchPlayersFinancialSummary();
    fetchLeaguePotBalance();
  }, [fetchPlayersFinancialSummary, fetchLeaguePotBalance]);

  const fetchPlayerDetails = async (playerId: number) => {
    try {
      setLoading(true);

      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

      const response = await fetch(`${apiUrl}/admin/bowler-finances/leagues/${leagueId}/players/${playerId}/finances`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch player details');
      }

      const data = await response.json();
      setSelectedPlayer(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleRefundPayment = async (paymentId: number, paymentType: string) => {
    if (!selectedPlayer) return;

    // Placeholder implementation
    fetch(`${BACKEND_URL || 'http://localhost:5000/api'}/admin/refunds/refund`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({ paymentId, paymentType })
    })
      .then(async response => {
        const data = await response.json();
        if (!response.ok) {
          // Show backend error message if available
          throw new Error(data.error || 'Failed to process refund');
        }
        alert('Refund processed successfully');
        //refresh player details
        fetchPlayerDetails(selectedPlayer.playerInfo.player_id);
      })
      .catch(error => {
        alert(error.message);
      });
  };

  const handleReversePayout = async (payoutId: number, reason: string) => {
    if (!selectedPlayer) return;
    fetch(`${BACKEND_URL || 'http://localhost:5000/api'}/admin/refunds/reverse-payout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({ payoutId, reason })
    })
      .then(response => {
        if (!response.ok) {
          throw new Error('Failed to reverse payout');
        }
        return response.json();
      })
      .then(data => {
        alert('Payout reversed successfully');
        //refresh player details
        fetchPlayerDetails(selectedPlayer.playerInfo.player_id);
      })
      .catch(error => {
        alert(error.message);
      });
  };

  const formatCurrency = (amount: number | string) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(num);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'active': return 'bg-blue-100 text-blue-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800 flex items-center justify-center">
        <div className="bg-white/10 backdrop-blur-lg rounded-xl p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white">{t('playerFinances.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800">
      {/* Header */}
      <div className="bg-white/10 backdrop-blur-lg border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={selectedPlayer ? () => setSelectedPlayer(null) : onBack}
                className="text-white hover:text-blue-300 transition duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h1 className="text-3xl font-bold text-white">
                {selectedPlayer ? `${selectedPlayer.playerInfo.player_name} - ${t('playerFinances.finances')}` : t('playerFinances.title')}
              </h1>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="bg-red-500/20 border border-red-400 rounded-lg p-4">
            <p className="text-red-200">{error}</p>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!selectedPlayer ? (
          /* Players Financial Summary */
          <div className="grid grid-cols-1 gap-6">
            {/* Overview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20">
                <h3 className="text-lg font-semibold text-white mb-2">{t('playerFinances.totalPlayers')}</h3>
                <p className="text-3xl font-bold text-blue-300">{playersFinancial.length}</p>
              </div>
              <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20">
                <h3 className="text-lg font-semibold text-white mb-2">{t('playerFinances.playersWithEarnings')}</h3>
                <p className="text-3xl font-bold text-green-300">
                  {playersFinancial.filter(p => p.balance > 0).length}
                </p>
              </div>
              <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20">
                <h3 className="text-lg font-semibold text-white mb-2">{t('playerFinances.totalLeagueBalance')}</h3>
                <p className={`text-3xl font-bold ${leaguePotBalance && leaguePotBalance.balance >= 0 ? 'text-green-300' : 'text-red-300'}`}>
                  {leaguePotBalance ? formatCurrency(leaguePotBalance.balance) : 'Loading...'}
                </p>
                {leaguePotBalance && (
                  <div className="mt-2 text-sm text-blue-200">
                    <p>{t('playerFinances.totalPot')}: {formatCurrency(leaguePotBalance.league_pot)}</p>
                    <p>{t('playerFinances.payoutsMade')}: {formatCurrency(leaguePotBalance.payouts_made)}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Players Table */}
            <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 overflow-hidden">
              <div className="px-6 py-4 border-b border-white/20">
                <h2 className="text-xl font-bold text-white">{t('playerFinances.playersOverview')}</h2>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-white/5">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-blue-200 uppercase tracking-wider">{t('playerFinances.player')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-blue-200 uppercase tracking-wider">{t('playerFinances.status')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-blue-200 uppercase tracking-wider">{t('playerFinances.totalPaid')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-blue-200 uppercase tracking-wider">{t('playerFinances.totalEarned')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-blue-200 uppercase tracking-wider">{t('playerFinances.balance')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-blue-200 uppercase tracking-wider">{t('playerFinances.actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/10">
                    {playersFinancial.map((player) => (
                      <tr key={player.player_id} className="hover:bg-white/5">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-white font-medium">{player.player_name}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(player.registration_status)}`}>
                            {t(`playerFinances.statuses.${player.registration_status}`)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-white">{formatCurrency(player.total_paid)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-white">{formatCurrency(player.total_earned)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className={`font-semibold ${player.balance >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {formatCurrency(player.balance)}
                            {player.balance > 0 && <span className="text-xs ml-1">({t('playerFinances.earning')})</span>}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => fetchPlayerDetails(player.player_id)}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm transition duration-200"
                          >
                            {t('playerFinances.viewDetails')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : (
          /* Individual Player Details */
          <div className="space-y-6">
            {/* Player Info Card */}
            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <h3 className="text-sm font-medium text-blue-200">{t('playerFinances.playerName')}</h3>
                  <p className="text-white font-semibold">{selectedPlayer.playerInfo.player_name}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-blue-200">{t('playerFinances.email')}</h3>
                  <p className="text-white">{selectedPlayer.playerInfo.email}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-blue-200">{t('playerFinances.registrationStatus')}</h3>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedPlayer.playerInfo.registration_status)}`}>
                    {t(`playerFinances.statuses.${selectedPlayer.playerInfo.registration_status}`)}
                  </span>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-blue-200">{t('playerFinances.team')}</h3>
                  <p className="text-white">{selectedPlayer.playerInfo.team_name || t('playerFinances.noTeam')}</p>
                </div>
              </div>
            </div>

            {/* Financial Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20">
                <h3 className="text-lg font-semibold text-red-300 mb-2">{t('playerFinances.totalPaidByPlayer')}</h3>
                <p className="text-2xl font-bold text-white">{formatCurrency(selectedPlayer.financialSummary.totalPaid)}</p>
              </div>
              <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20">
                <h3 className="text-lg font-semibold text-green-300 mb-2">{t('playerFinances.totalEarnedByPlayer')}</h3>
                <p className="text-2xl font-bold text-white">{formatCurrency(selectedPlayer.financialSummary.totalEarned)}</p>
              </div>
              <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20">
                <h3 className="text-lg font-semibold text-blue-200 mb-2">{t('playerFinances.playerBalance')}</h3>
                <p className={`text-2xl font-bold ${selectedPlayer.financialSummary.isEarning ? 'text-green-400' : 'text-red-400'}`}>
                  {formatCurrency(selectedPlayer.financialSummary.balance)}
                  {selectedPlayer.financialSummary.isEarning && (
                    <span className="text-sm ml-2 text-green-300">🎉 {t('playerFinances.earning')}</span>
                  )}
                </p>
              </div>
              <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20">
                <h3 className="text-lg font-semibold text-blue-200 mb-2">{t('playerFinances.paymentStatus')}</h3>
                <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${selectedPlayer.playerInfo.payment_complete ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                  {selectedPlayer.playerInfo.payment_complete ? t('playerFinances.complete') : t('playerFinances.pending')}
                </span>
              </div>
            </div>

            {/* Payment History */}
            <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 overflow-hidden">
              <div className="px-6 py-4 border-b border-white/20">
                <h2 className="text-xl font-bold text-white">💳 {t('playerFinances.paymentHistory')}</h2>
                <p className="text-blue-200 text-sm">{t('playerFinances.moneyPaidBy')}</p>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-white/5">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-blue-200 uppercase tracking-wider">{t('playerFinances.date')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-blue-200 uppercase tracking-wider">{t('playerFinances.type')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-blue-200 uppercase tracking-wider">{t('playerFinances.item')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-blue-200 uppercase tracking-wider">{t('playerFinances.amount')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-blue-200 uppercase tracking-wider">{t('playerFinances.method')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-blue-200 uppercase tracking-wider">{t('playerFinances.actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/10">
                    {selectedPlayer.paymentHistory.map((payment) => (
                      <tr key={payment.id} className="hover:bg-white/5">
                        <td className="px-6 py-4 whitespace-nowrap text-white">
                          {formatDate(payment.paid_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                            {payment.payment_type}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-white">{payment.item_name}</td>
                        {payment.refunded ? (
                          <td className="px-6 py-4 whitespace-nowrap text-red-400 font-semibold line-through">
                            {formatCurrency(payment.amount)}
                          </td>
                        ) : (
                          <td className="px-6 py-4 whitespace-nowrap text-white font-semibold">
                            {formatCurrency(payment.amount)}
                          </td>
                        )}
                        <td className="px-6 py-4 whitespace-nowrap text-white">
                          {payment.payment_method}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {payment.refunded ? (
                            <span className="text-red-400 font-semibold">{t('playerFinances.paymentRefunded')}</span>
                          ) : (
                            <button
                              onClick={() => openRefundModal(payment.id, payment.payment_type)}
                              className="bg-yellow-600 hover:bg-yellow-700 text-white px-3 py-1 rounded text-sm transition duration-200"
                            >
                              {t('playerFinances.refund')}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {selectedPlayer.paymentHistory.length === 0 && (
                  <div className="text-center py-8 text-blue-200">
                    {t('playerFinances.noPaymentHistory')}
                  </div>
                )}
              </div>
            </div>

            {/* Refund Modal Component */}
            {showRefundModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
                  <h2 className="text-xl font-bold mb-4 text-yellow-700">{t('playerFinances.refundModal.title')}</h2>
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={closeRefundModal}
                      className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-3 py-1 rounded text-sm transition duration-200"
                    >
                      {t('playerFinances.refundModal.cancel')}
                    </button>
                    <button
                      onClick={confirmRefundPayment}
                      className="bg-yellow-600 hover:bg-yellow-700 text-white px-3 py-1 rounded text-sm transition duration-200"
                    >
                      {t('playerFinances.refundModal.confirm')}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Payout History */}
            <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 overflow-hidden">
              <div className="px-6 py-4 border-b border-white/20">
                <h2 className="text-xl font-bold text-white">💸 {t('playerFinances.payoutHistory')}</h2>
                <p className="text-blue-200 text-sm">{t('playerFinances.moneyPaidTo')}</p>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-white/5">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-blue-200 uppercase tracking-wider">{t('playerFinances.date')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-blue-200 uppercase tracking-wider">{t('playerFinances.source')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-blue-200 uppercase tracking-wider">{t('playerFinances.amount')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-blue-200 uppercase tracking-wider">{t('playerFinances.notes')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-blue-200 uppercase tracking-wider">{t('playerFinances.actions')}</th>
                    </tr>
                  </thead>
                  {selectedPlayer.payoutHistory.map((payout) => (
                    <tr key={payout.id} className="hover:bg-white/5">
                      <td className="px-6 py-4 whitespace-nowrap text-white">
                        {formatDate(payout.payout_date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          {payout.source}
                        </span>
                      </td>
                      {payout.overturned ? (
                        <td className="px-6 py-4 whitespace-nowrap text-red-400 font-semibold line-through">
                          {formatCurrency(payout.amount)}
                        </td>
                      ) : (
                        <td className="px-6 py-4 whitespace-nowrap text-green-400 font-semibold">
                          {formatCurrency(payout.amount)}
                        </td>
                      )}
                      <td className="px-6 py-4 text-white">{payout.notes || t('playerFinances.noNotes')}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {payout.overturned ? (
                          <span className="text-red-400 font-semibold">{t('playerFinances.payoutReversed')}</span>
                        ) : (
                          <button
                            onClick={() => openReverseModal(payout.id)}
                            className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm transition duration-200"
                          >
                            {t('playerFinances.reverse')}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </table>

                {selectedPlayer.payoutHistory.length === 0 && (
                  <div className="text-center py-8 text-blue-200">
                    {t('playerFinances.noPayoutHistory')}
                  </div>
                )}
              </div>

            </div>
            {/* Modal Component */}
            {showReverseModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
                  <h2 className="text-xl font-bold mb-4 text-red-700">{t('playerFinances.reverseModal.title')}</h2>
                  <label className="block mb-2 text-sm font-medium text-gray-700">{t('playerFinances.reverseModal.notesLabel')}</label>
                  <textarea
                    className="w-full border rounded p-2 mb-4"
                    rows={3}
                    value={reverseNotes}
                    onChange={e => setReverseNotes(e.target.value)}
                    placeholder={t('playerFinances.reverseModal.placeholder')}
                  />
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={closeReverseModal}
                      className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded"
                    >
                      {t('playerFinances.reverseModal.cancel')}
                    </button>
                    <button
                      onClick={confirmReversePayout}
                      disabled={!reverseNotes.trim()}
                      className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded font-semibold"
                    >
                      {t('playerFinances.reverseModal.confirm')}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PlayerFinances;